import { Label, Switch } from '@playcastdotio/Shadcn';
import { faEnvelope, faMobileButton } from '@fortawesome/pro-solid-svg-icons';
import { Content } from './Content';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { NotificationTypes } from './types';

export const Preferences = ({
  initialPreferences,
  savePreferences,
}: {
  initialPreferences: { phone: Partial<NotificationTypes>; email: Partial<NotificationTypes> };
  savePreferences: (phonePreferences: NotificationTypes, emailPreferences: NotificationTypes) => void;
}) => {
  const [phonePreferences, setPhonePreferences] = useState<NotificationTypes>({
    applicationUpdates: initialPreferences.phone.applicationUpdates ?? true,
    feedbackSurveys: initialPreferences.phone.feedbackSurveys ?? true,
    newsletter: initialPreferences.phone.newsletter ?? true,
    playcastEvents: initialPreferences.phone.playcastEvents ?? true,
    hostMode: initialPreferences.phone.hostMode ?? true,
  });

  const [emailPreferences, setEmailPreferences] = useState<NotificationTypes>({
    applicationUpdates: initialPreferences.email.applicationUpdates ?? true,
    feedbackSurveys: initialPreferences.email.feedbackSurveys ?? true,
    newsletter: initialPreferences.email.newsletter ?? true,
    playcastEvents: initialPreferences.email.playcastEvents ?? true,
    hostMode: initialPreferences.email.hostMode ?? true,
  });

  const preferences: { keyName: keyof NotificationTypes; title: string; description: string }[] = [
    {
      keyName: 'applicationUpdates',
      title: 'Application updates',
      description: 'Receive texts or emails about new features and updates',
    },
    {
      keyName: 'playcastEvents',
      title: 'Playcast Events',
      description: 'Receive texts or emails about Playcast hosted events',
    },
    {
      keyName: 'feedbackSurveys',
      title: 'Feedback Surveys',
      description: 'Receive texts or emails asking for feedback to improve the app',
    },
    {
      keyName: 'newsletter',
      title: 'Newsletter',
      description: 'Receive texts or emails with Playcast videos and announcements',
    },
    {
      keyName: 'hostMode',
      title: 'Host Mode Notifications',
      description: 'Receive alerts when your machine enters or exits host mode',
    },
  ];

  const handlePhonePreferenceChanged = (key: keyof NotificationTypes, enabled: boolean) => {
    setPhonePreferences((prev) => ({ ...prev, [key]: enabled }));
  };
  const handleEmailPreferenceChanged = (key: keyof NotificationTypes, enabled: boolean) => {
    setEmailPreferences((prev) => ({ ...prev, [key]: enabled }));
  };

  useEffect(() => {
    return () => {
      savePreferences({ ...phonePreferences }, { ...emailPreferences });
    };
  }, [emailPreferences, phonePreferences, savePreferences]);

  return (
    <Content title='Preferences'>
      <div className='flex h-full flex-1 flex-col space-y-4'>
        <Label className='text-xl'>Notification Preferences</Label>
        <div className='flex w-full justify-end gap-8 pb-2'>
          <FontAwesomeIcon icon={faMobileButton} className='w-11 text-2xl text-gray-300' />
          <FontAwesomeIcon icon={faEnvelope} className='w-11 text-2xl text-gray-300' />
        </div>
        {preferences.map((preference, i) => (
          <PreferenceSwitch
            key={i}
            keyName={preference.keyName}
            title={preference.title}
            description={preference.description}
            isEmailEnabled={emailPreferences[preference.keyName]}
            isPhoneEnabled={phonePreferences[preference.keyName]}
            onChangeEmail={handleEmailPreferenceChanged}
            onChangeMobile={handlePhonePreferenceChanged}
          />
        ))}
      </div>
    </Content>
  );
};

const PreferenceSwitch = ({
  keyName,
  title,
  description,
  isPhoneEnabled,
  isEmailEnabled,
  onChangeMobile,
  onChangeEmail,
}: {
  keyName: keyof NotificationTypes;
  title: string;
  description: string;
  isPhoneEnabled: boolean;
  isEmailEnabled: boolean;
  onChangeMobile: (keyname: keyof NotificationTypes, enabled: boolean) => void;
  onChangeEmail: (keyname: keyof NotificationTypes, enabled: boolean) => void;
}) => {
  return (
    <div className='flex border-b border-gray-800 pb-1'>
      <div className='flex flex-col'>
        <Label htmlFor={title} className='cursor-pointer text-lg'>
          {title}
        </Label>
        <span className='text-xs'>{description}</span>
      </div>
      <div className='ml-auto flex gap-8'>
        <Switch
          id={title}
          checked={isPhoneEnabled}
          onCheckedChange={(e) => onChangeMobile(keyName, e)}
          className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
        />
        <Switch
          id={title}
          checked={isEmailEnabled}
          onCheckedChange={(e) => onChangeEmail(keyName, e)}
          className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
        />
      </div>
    </div>
  );
};
